import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, Calculator } from "lucide-react";
import { Link } from "react-router-dom";

export default function AgentPlan() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/agent/plan").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Commission & deposit plan</h1>
        <p className="text-sm text-muted-foreground">Live policy — always matches current company settings.</p>
      </div>
      <Scenarios />

      <Link to="/app/simulator" className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 hover:bg-primary/10 transition-colors" data-testid="simulator-link">
        <Calculator className="w-5 h-5 text-primary" />
        <div><p className="text-sm font-semibold">Earnings simulator</p><p className="text-xs text-muted-foreground">Enter bags per discount level and see your month's earnings instantly</p></div>
      </Link>

      <Card className="p-5">
        <h3 className="font-semibold mb-3">Your commission by product</h3>
        <div className="space-y-2">
          {data.products.map((p) => (
            <div key={p.id} className="flex justify-between text-sm border-b border-border/50 pb-2 last:border-0">
              <span>{p.name} · {p.pack}</span>
              <span className="font-medium">{p.list_price} AED · <span className="text-primary">{p.discount_commissions[0]} comm</span></span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">The more you discount, the less you earn — commission falls faster than the price. Max discount {data.max_discount_level}%.</p>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold mb-3">Monthly accelerator</h3>
        <div className="space-y-2 text-sm">
          {data.accelerator_tiers.map((t, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-muted-foreground">
                {t.min.toLocaleString()}{t.max ? ` – ${t.max.toLocaleString()}` : "+"} AED
              </span>
              <span className="font-medium">{t.boost === 0 ? "Base rate" : `+${Math.round(t.boost * 100)}%`}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 space-y-2 text-sm">
        <h3 className="font-semibold mb-2">Extra bonuses</h3>
        <p><span className="font-medium">New-customer bonus:</span> {data.new_customer_bonus} AED for every new customer whose first paid order is ≥ {data.new_customer_threshold.toLocaleString()} AED.</p>
        <p><span className="font-medium">Account ownership:</span> you keep earning on repeat orders for {data.ownership_window_months} months.</p>
        <p><span className="font-medium">Star agent:</span> the month's top seller earns an extra {data.star_agent_bonus} AED.</p>
        <p><span className="font-medium">Above list:</span> keep {Math.round(data.above_split * 100)}% of every dirham collected above list price.</p>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold mb-3">Security deposit tiers</h3>
        <div className="space-y-2 text-sm">
          {data.deposit_tiers.map((t) => (
            <div key={t.name} className="flex justify-between">
              <span className="text-muted-foreground">{t.name} · {t.weekly_max ? `up to ${t.weekly_max}` : "150+"} bags/wk</span>
              <span className="font-medium">{t.deposit.toLocaleString()} AED</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Your deposit equals your stock credit limit and is fully refundable when you leave in good standing.
        </p>
      </Card>

      <Card className="p-5 text-sm space-y-1.5">
        <h3 className="font-semibold mb-2">How & when you're paid</h3>
        <p className="text-muted-foreground">• Commission paid monthly by bank transfer, on/before the 10th of the following month.</p>
        <p className="text-muted-foreground">• Released only after the customer pays in full.</p>
        <p className="text-muted-foreground">• Returns or cancelled orders are deducted from your next payout.</p>
      </Card>
    </div>
  );
}

function Scenarios() {
  const [sc, setSc] = useState(null);
  useEffect(() => { api.get("/agent/scenarios").then((r) => setSc(r.data)); }, []);
  if (!sc) return null;
  return (
    <Card className="p-4" data-testid="earnings-scenarios">
      <h3 className="font-semibold text-sm">What agents can earn per month</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-3">{sc.note}</p>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground uppercase tracking-wider border-b border-border">
              <th className="text-left py-1.5 px-1">Profile</th>
              <th className="text-right py-1.5 px-1">Sales / mo</th>
              <th className="text-right py-1.5 px-1">Base</th>
              <th className="text-right py-1.5 px-1">Tier</th>
              <th className="text-right py-1.5 px-1">Accel.</th>
              <th className="text-right py-1.5 px-1">New cust.</th>
              <th className="text-right py-1.5 px-1">Total / mo</th>
            </tr>
          </thead>
          <tbody>
            {sc.rows.map((r) => (
              <tr key={r.profile} className="border-b border-border/40" data-testid={`scenario-${r.profile.split(" ")[0].toLowerCase()}`}>
                <td className="py-2 px-1 font-medium whitespace-nowrap">{r.profile}</td>
                <td className="py-2 px-1 text-right">{r.collected_sales.toLocaleString()}</td>
                <td className="py-2 px-1 text-right">{r.base_commission.toLocaleString()}</td>
                <td className="py-2 px-1 text-right">{r.tier_boost ? `+${Math.round(r.tier_boost * 100)}%` : "—"}</td>
                <td className="py-2 px-1 text-right">{r.accelerator_bonus.toLocaleString()}</td>
                <td className="py-2 px-1 text-right">{r.new_customers}× = {r.new_customer_bonus}</td>
                <td className="py-2 px-1 text-right font-semibold text-primary">{r.total.toLocaleString()} AED</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
